import { observable } from '@legendapp/state';
import { onChangeRemote } from '@legendapp/state/sync';
import { syncedSupabase, configureSyncedSupabase } from '@legendapp/state/sync-plugins/supabase';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabase';
import { apiConfig } from '../lib/apiConfig';
import { auth$, getAuthHeaders } from './auth$';
import { ObservablePersistLocalStorage } from '@legendapp/state/persist-plugins/local-storage';
import { createSyncCreate, createSyncUpdate, defaultSyncConfig, shouldForceFullSync } from './syncHelpers';
import { getProgress, getIsArchived, toggleArchive } from './readingProgress$';
import { normalizeTag } from '../utils/normalizeTag';
import { articleLists$, isArticleInList, getArticleListIds, removeArticleFromAllLists } from './articleLists$';
import {
  trackEvent,
  setOnceUserProperties,
  incrementUserProperties,
} from '../lib/analytics';
import {
  getContent as contentCacheGet,
  setContent as contentCacheSet,
  bulkSetContent as contentCacheBulkSet,
  deleteContent as contentCacheDelete,
  hasCachedContentSync,
  markCachedSync,
  unmarkCachedSync,
} from '../lib/contentCache';

// ============================================================================
// Storage URL Resolution
// ============================================================================
// PDF/EPUB files are stored in private Supabase Storage buckets.
// Instead of storing signed URLs (which expire after 1 hour), we store a
// permanent storage path with a "storage:" prefix:
//   storage:reedle-pdfs/userId/fileId.pdf
//   storage:reedle-epubs/userId/fileId.epub
//
// resolveArticleUrl() detects this prefix and generates a fresh signed URL
// on demand. Normal URLs pass through unchanged.

/**
 * Resolve an article URL — if it's a storage path, generate a fresh signed URL
 * @param {string} url - Either a normal URL or "storage:{bucket}/{path}"
 * @returns {Promise<string>} Resolved URL ready for use
 */
export const resolveArticleUrl = async (url) => {
  if (!url) return url;

  // New format: "storage:bucket/path" — parse and sign
  if (url.startsWith('storage:')) {
    const storagePart = url.slice('storage:'.length); // "reedle-pdfs/userId/fileId.pdf"
    const slashIndex = storagePart.indexOf('/');
    if (slashIndex === -1) {
      console.error('[resolveArticleUrl] Invalid storage path:', url);
      return url;
    }

    const bucket = storagePart.slice(0, slashIndex); // "reedle-pdfs"
    const path = storagePart.slice(slashIndex + 1);  // "userId/fileId.pdf"

    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, 3600); // 1 hour

    if (error) {
      console.error('[resolveArticleUrl] Failed to sign URL:', error);
      return url;
    }

    return data.signedUrl;
  }

  // Legacy fallback: expired signed URLs from before storage: prefix migration
  // Extract bucket/path from URLs like .../object/sign/reedle-pdfs/userId/file.pdf?token=...
  const legacyMatch = url.match(/\/(?:object\/sign|object\/public)\/(reedle-(?:pdfs|epubs))\/([^?]+)/);
  if (legacyMatch) {
    const bucket = legacyMatch[1];
    const path = legacyMatch[2];

    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, 3600);

    if (error) {
      console.error('[resolveArticleUrl] Failed to re-sign legacy URL:', error);
      return url;
    }

    console.log('[resolveArticleUrl] Re-signed legacy URL for', bucket, path);
    return data.signedUrl;
  }

  // Normal URL — pass through unchanged
  return url;
};

// ============================================================================
// Block Version System
// ============================================================================
// Version 1: Original block indexing (lists as single block, HR/IMG not trackable)
// Version 2: New block indexing (list items as separate blocks, HR/IMG trackable)
//
// Articles created before this cutoff date use version 1 indexing.
// Articles created after use version 2 indexing.
// This ensures backward compatibility with existing audio, translations, and progress data.

export const BLOCK_VERSION_CUTOFF_DATE = '2026-01-17T00:00:00.000Z';
export const BLOCK_VERSION_CURRENT = 2;

/**
 * Get the block version for an article
 * @param {Object} article - Article object with created_at field
 * @returns {number} 1 for legacy articles, 2 for new articles
 */
export const getBlockVersion = (article) => {
  if (!article) return BLOCK_VERSION_CURRENT;

  // Explicit version field takes precedence
  if (article.block_version !== undefined && article.block_version !== null) {
    return article.block_version;
  }

  // Fall back to timestamp-based detection
  if (!article.created_at) return BLOCK_VERSION_CURRENT;

  const createdAt = new Date(article.created_at);
  const cutoff = new Date(BLOCK_VERSION_CUTOFF_DATE);

  return createdAt < cutoff ? 1 : 2;
};

// Configure syncedSupabase globally with ID generation and sync settings
// Using 'last-sync' mode for efficient incremental sync.
// When force-full-sync flag is set (after forceUpdate/forceCleanAndRelogin),
// changesSince is undefined → forces a full sync to recover from partial data.
configureSyncedSupabase({
  generateId: () => uuidv4(),
  // Enable incremental sync - only fetch changes since last sync
  changesSince: shouldForceFullSync ? undefined : 'last-sync',
  // Map to Supabase column names
  fieldCreatedAt: 'created_at',
  fieldUpdatedAt: 'updated_at',
  // Enable soft deletes (boolean column in Supabase)
  fieldDeleted: 'deleted',
});

// Fix for foreign key errors: clear list_id if the list doesn't exist
const fixArticleForeignKey = (input) => ({
  ...input,
  list_id: null, // Move back to Inbox if list doesn't exist
});

// ============================================================================
// Slim Metadata Sync Configuration
// ============================================================================
// For performance, we sync only metadata fields initially.
// Heavy content fields (content, extra_metadata, hidden_block_indices) are
// loaded on-demand when user opens an article.
//
// Fields synced (slim):
//   id, user_id, url, title, excerpt, site_name, status, error_message,
//   is_starred, is_archived, rating, list_id, video_type, video_id,
//   transcript_language, created_at, updated_at, deleted,
//   last_viewed_at, cover_url, comic_cover_url, epub_toc, tags
//
// Fields excluded (lazy-loaded):
//   content, extra_metadata, hidden_block_indices

const SLIM_ARTICLE_FIELDS = `
  id, user_id, url, title, excerpt, site_name, status, error_message,
  is_starred, is_archived, rating, list_id, video_type, video_id, audio_type,
  transcript_language, created_at, updated_at, deleted,
  last_viewed_at, cover_url, comic_cover_url, epub_toc, tags, example_id,
  mindmap_markdown, mindmap_status,
  summary_content, summary_status, summary_error
`.replace(/\s+/g, ' ').trim();

// Articles store with Supabase sync (local-first)
// Using syncedSupabase for automatic sync with local persistence
// NOTE: No filter here - RLS handles permissions:
//   - User can see own articles (user_id = auth.uid())
//   - User can see shared list articles (list_id in lists where user is member)
export const articles$ = observable(syncedSupabase({
  supabase,
  collection: 'reedle_articles',
  // Slim sync: only fetch metadata fields, content loaded on-demand
  select: (from) => from.select(SLIM_ARTICLE_FIELDS),
  // Custom create/update with error handling and foreign key auto-fix
  // IMPORTANT: excludeFieldsOnUpdate prevents stale local 'content' from overwriting
  // server-processed content (e.g., Gemini-cleaned YouTube subtitles, RIL processor output).
  // Frontend can still INSERT with content (snippets, markdown files), but cannot UPDATE it.
  // Content is fetched on-demand via loadArticleContent() and stored locally for offline reading.
  create: createSyncCreate('reedle_articles', { onForeignKeyError: fixArticleForeignKey }),
  update: createSyncUpdate('reedle_articles', {
    onForeignKeyError: fixArticleForeignKey,
    excludeFieldsOnUpdate: ['content'],
  }),
  // Use default sync config
  ...defaultSyncConfig,
  // Enable realtime updates for shared hidden blocks and article status
  realtime: true,
  // Persist data and pending changes locally
  persist: {
    name: 'reedle-articles',
    plugin: ObservablePersistLocalStorage,
    retrySync: true, // Retry failed syncs when back online
  },
}));

// Version counter — incremented on every article creation to force reactive
// re-evaluation of useSelector callbacks that use articles$.get(), which may
// not fire when a brand-new key is added to the syncedSupabase observable.
export const articlesVersion$ = observable(0);

// Older persisted slim rows predate example_id. Resolve the requested identity
// from the server and hydrate it as remote data, without re-saving user content.
export const findExampleArticle = async (exampleId, userId) => {
  if (!userId || auth$.user.get()?.id !== userId) throw new Error('Article account changed');
  const local = Object.values(articles$.get() || {}).find(article => article?.id
    && article.user_id === userId && article.example_id === exampleId
    && !article.deleted && article.status === 'ready');
  if (local) return local;

  const { data, error } = await supabase.from('reedle_articles')
    .select(SLIM_ARTICLE_FIELDS)
    .eq('user_id', userId).eq('example_id', exampleId).eq('status', 'ready')
    .or('deleted.is.null,deleted.eq.false')
    .order('created_at', { ascending: false }).limit(1).maybeSingle();
  if (error) throw error;
  if (auth$.user.get()?.id !== userId) throw new Error('Article account changed');
  if (!data) return null;
  const current = articles$[data.id].peek();
  if (current?.deleted) return null;
  onChangeRemote(() => {
    // Preserve pending local metadata edits when only the identity was missing.
    if (current) articles$[data.id].example_id.set(data.example_id);
    else articles$[data.id].set(data);
  });
  return articles$[data.id].peek();
};

// Helper functions for article operations
// With syncedSupabase, we just set values and sync happens automatically

/**
 * Track article_saved event with source type detection
 * @param {Object} article - The created article
 * @param {string} sourceType - The source type (url, youtube, bilibili, pdf, epub, snippet, markdown, batch_merge)
 */
const trackArticleSaved = (article, sourceType) => {
  try {
    // Extract domain from URL if present
    let urlDomain = null;
    if (article.url) {
      try {
        urlDomain = new URL(article.url).hostname;
      } catch {
        // Ignore URL parsing errors (e.g., for signed URLs)
      }
    }

    trackEvent('article_saved', {
      article_id: article.id,
      source_type: sourceType,
      url_domain: urlDomain,
      has_transcript: Boolean(article.video_id), // Video articles have transcripts
    });

    // First-time event
    setOnceUserProperties({ first_article_saved_at: new Date().toISOString() });
    trackEvent('first_article_saved', {
      article_id: article.id,
      source_type: sourceType,
    });

    // Increment counter
    incrementUserProperties({ total_articles_saved: 1 });
  } catch (err) {
    console.error('[Analytics] Failed to track article_saved:', err);
  }
};

/**
 * Create a pending article (non-blocking URL submission)
 * Article is created with status='pending', Edge Function will fetch content
 * @param {string} url - The URL to fetch
 * @param {string|null} listId - The list to add article to (null = Inbox)
 * @param {string|null} articleId - Optional pre-generated ID (for share extension reconciliation)
 */
export const createPendingArticle = (url, listId = null, articleId = null) => {
  const userId = auth$.user.get()?.id;
  if (!userId) throw new Error('User not authenticated');

  const id = articleId || uuidv4();
  const now = new Date().toISOString();

  const newArticle = {
    id,
    user_id: userId,
    url: url.trim(),
    title: 'Fetching...', // Placeholder until Edge Function fetches
    content: '',
    excerpt: '',
    site_name: '',
    status: 'pending', // Triggers Edge Function via database trigger
    error_message: null,
    is_starred: false,
    is_archived: false,
    rating: 0,
    list_id: listId,
    created_at: now,
    updated_at: now,
  };

  // Just set in observable - syncedSupabase syncs to DB, trigger calls Edge Function
  articles$[id].set(newArticle);
  articlesVersion$.set(v => v + 1);

  // Track article saved event
  trackArticleSaved(newArticle, 'url');

  return newArticle;
};

/**
 * Retry a failed article fetch
 * For EPUB articles, regenerates signed URL and calls Express server
 * For other articles, sets status to 'pending' to trigger Edge Function
 */
export const retryArticle = async (articleId) => {
  const article = articles$[articleId].get();
  if (!article) return;

  // EPUB articles need special handling - call Express server directly
  if (article.site_name === 'EPUB') {
    await retryEpubArticle(articleId, article);
    return;
  }

  // PDF and URL articles - set status to pending, Edge Function will process
  articles$[articleId].assign({
    status: 'pending',
    error_message: null,
    error_code: null,
    updated_at: new Date().toISOString(),
  });
};

/**
 * Retry a failed EPUB article
 * Uses resolveArticleUrl() to sign the storage path on-demand — no brittle regex needed
 */
const retryEpubArticle = async (articleId, article) => {
  const userId = auth$.user.get()?.id;
  if (!userId) throw new Error('User not authenticated');

  // Update status to pending
  articles$[articleId].assign({
    status: 'pending',
    error_message: null,
    updated_at: new Date().toISOString(),
  });

  try {
    // Resolve the storage path to a fresh signed URL
    // Works for both new "storage:" paths and legacy signed URLs (passthrough)
    let signedUrl = article.url;
    if (article.url.startsWith('storage:')) {
      signedUrl = await resolveArticleUrl(article.url);
    } else {
      // Legacy: extract storage path from old signed URL and re-sign
      try {
        const parsedUrl = new URL(article.url);
        const pathMatch = parsedUrl.pathname.match(/\/reedle-epubs\/(.+)$/);
        if (pathMatch) {
          const { data: urlData, error: urlError } = await supabase.storage
            .from('reedle-epubs')
            .createSignedUrl(pathMatch[1], 3600);
          if (!urlError && urlData) {
            signedUrl = urlData.signedUrl;
          }
        }
      } catch (e) {
        console.error('Failed to parse legacy EPUB URL:', e);
      }
    }

    // Call Express server to process
    const response = await fetch(`${REEDLE_SERVER_URL}/process-epub`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({
        id: articleId,
        url: signedUrl,
        user_id: userId,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'EPUB processing failed');
    }

    console.log('EPUB retry processing completed');
  } catch (error) {
    console.error('EPUB retry error:', error);
    articles$[articleId].assign({
      status: 'failed',
      error_message: error.message || 'Failed to process EPUB',
      updated_at: new Date().toISOString(),
    });
  }
};

/**
 * Create a snippet (offline-capable)
 * Note: URL articles require server-side processing, so they can't be created offline
 * @param {string|null} listId - The list to add snippet to (null = Inbox)
 */
export const createSnippet = ({ title = '', content = '', listId = null, exampleId = null, expectedUserId = null }) => {
  const userId = auth$.user.get()?.id;
  if (!userId) throw new Error('User not authenticated');

  if (expectedUserId && expectedUserId !== userId) throw new Error('Article account changed');

  const now = new Date();
  const defaultTitle = title.trim() ||
    `Snippet ${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  const excerpt = content.split('\n').find(line => line.trim())?.trim().slice(0, 200) || '';

  const id = uuidv4();
  const newArticle = {
    id,
    user_id: userId,
    url: null, // Snippets have no URL
    title: defaultTitle,
    content: content.trim(),
    excerpt,
    site_name: exampleId ? 'Reedle' : 'Snippet',
    example_id: exampleId,
    status: 'ready', // Snippets are ready immediately
    rating: 0,
    list_id: listId,
    created_at: now.toISOString(),
    updated_at: now.toISOString(),
  };

  // Just set in observable - syncedSupabase handles the rest!
  // Content is included for INSERT sync to Supabase.
  // Offline cache goes to contentCache (separate IDB).
  articles$[id].set(newArticle);
  articlesVersion$.set(v => v + 1);

  // Dual-write: store content in separate cache for offline reading
  contentCacheSet(id, {
    content: newArticle.content,
    extra_metadata: null,
    hidden_block_indices: null,
  }).then(ok => { if (ok) markCachedSync(id); })
    .catch(e => console.warn('[articles$] contentCache write failed for snippet:', e));

  // Track article saved event
  trackArticleSaved(newArticle, exampleId ? 'example' : 'snippet');

  return newArticle;
};


/**
 * Create an article from a text file (TXT/Markdown)
 * Similar to createSnippet but with distinct site_name for file imports
 * @param {Object} options
 * @param {string} options.fileName - Original file name (used as title)
 * @param {string} options.content - File content
 * @param {string|null} options.listId - Target list (null = Inbox)
 * @returns {Object} The created article
 */
export const createTextFileArticle = ({ fileName, content, listId = null }) => {
  const userId = auth$.user.get()?.id;
  if (!userId) throw new Error('User not authenticated');

  const now = new Date();
  // Remove file extension for title
  const title = fileName.replace(/\.(txt|md|markdown)$/i, '').trim() ||
    `Text ${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  const excerpt = content.split('\n').find(line => line.trim())?.trim().slice(0, 200) || '';

  const id = uuidv4();
  const newArticle = {
    id,
    user_id: userId,
    url: null, // Text files have no URL
    title,
    content: content.trim(),
    excerpt,
    site_name: 'Markdown', // Distinct from 'Snippet' for file imports
    status: 'ready', // Text files are ready immediately
    rating: 0,
    list_id: listId,
    created_at: now.toISOString(),
    updated_at: now.toISOString(),
  };

  // Just set in observable - syncedSupabase handles the rest!
  // Content included for INSERT sync to Supabase.
  articles$[id].set(newArticle);
  articlesVersion$.set(v => v + 1);

  // Dual-write: store content in separate cache for offline reading
  contentCacheSet(id, {
    content: newArticle.content,
    extra_metadata: null,
    hidden_block_indices: null,
  }).then(ok => { if (ok) markCachedSync(id); })
    .catch(e => console.warn('[articles$] contentCache write failed for text file:', e));

  // Track article saved event
  trackArticleSaved(newArticle, 'markdown');

  return newArticle;
};

/**
 * Create a video article (YouTube, etc.)
 * @param {Object} options
 * @param {string} options.url - Original video URL
 * @param {string} options.videoId - Video ID (e.g., YouTube video ID)
 * @param {string} options.videoType - 'youtube' | 'bilibili'
 * @param {string} options.title - Video title
 * @param {string} options.content - Transcript content (markdown with timecodes)
 * @param {string} options.language - Transcript language name
 * @param {string} options.languageCode - Transcript language code
 * @param {boolean} options.isGenerated - Whether transcript is auto-generated
 * @param {string|null} options.listId - Target list (null = Inbox)
 * @param {string|null} options.coverUrl - Video thumbnail URL (for Bilibili, etc.)
 * @returns {Object} Created article
 */
export const createVideoArticle = async ({
  url,
  videoId,
  videoType,
  title,
  content,
  languageCode,
  listId = null,
  coverUrl = null,
}) => {
  const userId = auth$.user.get()?.id;
  if (!userId) throw new Error('User not authenticated');

  const id = uuidv4();
  const now = new Date().toISOString();
  const excerpt = content.split('\n').find(line => line.trim())?.trim().slice(0, 200) || '';

  const newArticle = {
    id,
    user_id: userId,
    url,
    title,
    content,
    excerpt,
    site_name: videoType === 'youtube' ? 'YouTube' : 'Bilibili',
    status: videoType === 'youtube' ? 'pending' : 'ready', // YouTube: pending triggers DB trigger → Edge Function → Render server; Bilibili: ready immediately
    video_type: videoType,
    video_id: videoId,
    transcript_language: languageCode,
    rating: 0,
    list_id: listId,
    cover_url: coverUrl,
    created_at: now,
    updated_at: now,
  };

  if (videoType === 'youtube') {
    // YouTube goes through the server-side processing pipeline (pending → render → ready).
    // Use direct DB insert (same as EPUB) to avoid the Legend-State localStorage-quota race:
    // persist failure must fail loud, not silently block the DB trigger from firing.
    // Also avoids client overwriting server's pending→ready status transition.
    const { error: insertError } = await supabase
      .from('reedle_articles')
      .insert(newArticle);
    if (insertError) {
      throw new Error(`Failed to create article: ${insertError.message}`);
    }
    // Do NOT call articles$[id].set() — realtime subscription will pick it up,
    // and server updates (status=ready, final content) must not be overwritten.
  } else {
    // Bilibili: content is inlined and status=ready from the start, no server processing.
    // Legend-State path is safe because there is no race with a server update.
    articles$[id].set(newArticle);
  }
  articlesVersion$.set(v => v + 1);

  // Dual-write: store content in separate cache for offline reading.
  // article_status is stored so loadArticleContent can detect stale pending→ready transitions
  // (e.g. YouTube raw transcript cached before Gemini processing completes).
  if (newArticle.content) {
    contentCacheSet(id, {
      content: newArticle.content,
      extra_metadata: null,
      hidden_block_indices: null,
      article_status: newArticle.status,
    }).then(ok => { if (ok) markCachedSync(id); })
      .catch(e => console.warn('[articles$] contentCache write failed for video:', e));
  }

  // Track article saved event (youtube or bilibili)
  trackArticleSaved(newArticle, videoType);

  // YouTube processing is now handled by the DB trigger pipeline:
  // article INSERT → DB trigger → Edge Function → Render server
  // No fire-and-forget HTTP needed — same reliable path as URL/PDF/Batch Merge articles.

  return newArticle;
};

/**
 * Create a batch merge article from multiple URLs
 * @param {string[]} urls - Array of URLs to merge
 * @param {string|null} customTitle - Optional custom title for merged article
 * @param {string|null} listId - Target list (null = Inbox)
 * @returns {Object} Created article
 */
export const createBatchMergeArticle = (urls, customTitle = null, listId = null) => {
  const userId = auth$.user.get()?.id;
  if (!userId) throw new Error('User not authenticated');
  if (!urls || urls.length === 0) throw new Error('No URLs provided');

  const id = uuidv4();
  const now = new Date().toISOString();

  const newArticle = {
    id,
    user_id: userId,
    url: urls[0], // First URL as primary
    title: customTitle || 'Merging articles...', // User input or placeholder
    content: '',
    excerpt: '',
    site_name: 'Batch Merge',
    status: 'pending', // Triggers Edge Function via database trigger
    error_message: null,
    is_starred: false,
    is_archived: false,
    rating: 0,
    list_id: listId,
    extra_metadata: {
      batch_merge: true,
      source_urls: urls,
      custom_title: customTitle,
    },
    created_at: now,
    updated_at: now,
  };

  // Just set in observable - syncedSupabase syncs to DB, trigger calls Edge Function
  articles$[id].set(newArticle);
  articlesVersion$.set(v => v + 1);

  // Track article saved event
  trackArticleSaved(newArticle, 'batch_merge');

  return newArticle;
};

/**
 * Delete an article (offline-capable)
 * Also removes article from all lists to maintain data consistency
 */
export const deleteArticle = (articleId) => {
  // Remove from all lists first (cleans up junction table)
  removeArticleFromAllLists(articleId);

  // Clean up content cache
  contentCacheDelete(articleId)
    .then(() => unmarkCachedSync(articleId))
    .catch(e => console.warn('[articles$] contentCache delete failed:', e));

  // Then soft-delete the article - syncedSupabase handles sync automatically
  articles$[articleId].delete();
};

/**
 * Get article by ID
 */
export const getArticleById = (articleId) => {
  return articles$[articleId].get();
};

/**
 * Get total article count for the current user (non-deleted).
 * Used for article storage limit enforcement.
 */
export const getArticleCount = () => {
  const articlesObj = articles$.get() || {};
  return Object.values(articlesObj).filter(a => a?.id && !a.deleted).length;
};

/**
 * Article counts per navigation destination, keyed by list id plus the two
 * synthetic destinations `all` and `inbox`.
 *
 * Inbox counts only the viewer's own articles: a shared list's articles stay
 * cached locally after the junction row is gone, and would otherwise surface as
 * someone else's article in your Inbox.
 *
 * Shared by the desktop sidebar and the phone list switcher so both read one
 * definition of "how many".
 */
export const getListArticleCounts = () => {
  const articlesObj = articles$.get() || {};
  const userId = auth$.user.get()?.id;
  const counts = { all: 0, inbox: 0 };

  Object.values(articleLists$.get() || {}).forEach(entry => {
    if (!entry?.list_id) return;
    counts[entry.list_id] = (counts[entry.list_id] || 0) + 1;
  });

  Object.values(articlesObj).forEach(article => {
    if (!article || article.deleted) return;
    if (getArticleListIds(article.id).length === 0) {
      if (article.user_id !== userId) return;
      counts.all++;
      counts.inbox++;
    } else {
      // Counted once in `all` even when the article sits in several lists.
      counts.all++;
    }
  });

  return counts;
};

/**
 * Get articles as array
 * @param {Object} options - Filter options
 * @param {string|null|'all'} options.listId - Filter by list ('all' = all, null = Inbox, string = specific list)
 * @param {boolean} options.archivedOnly - Show only archived articles (for Archived tab)
 * @param {string} options.sortBy - Sort by 'created_at' (default) or 'last_viewed_at'
 */
export const getArticlesArray = ({ listId = undefined, archivedOnly = false, sortBy = 'created_at' } = {}) => {
  const articlesObj = articles$.get() || {};
  const currentUserId = auth$.user.get()?.id;

  return Object.values(articlesObj)
    .filter(article => {
      // Filter out invalid entries (no id or deleted)
      if (!article || !article.id || article.deleted) return false;

      // Filter by list:
      // - 'all' = show all articles (own inbox + all lists user is member of)
      // - null = Inbox only (own articles with no list)
      // - string (uuid) = specific list (uses junction table for multi-list support)
      if (listId === 'all') {
        // All view: show everything (RLS already filters to own + shared list articles)
        // No additional filtering needed
      } else if (listId === null) {
        // Inbox: only show own articles with no list associations
        const articleListIds = getArticleListIds(article.id);
        if (articleListIds.length > 0) return false;
        if (article.user_id !== currentUserId) return false;
      } else if (listId !== undefined) {
        // Specific list: show articles in that list (via junction table, supports multi-list)
        if (!isArticleInList(article.id, listId)) return false;
      }

      // Filter by archive status (per-user, from readingProgress$):
      // - archivedOnly=true: show ONLY archived articles (Archived tab)
      // - archivedOnly=false: show ONLY non-archived articles (Articles tab)
      const archived = getIsArchived(article.id);
      if (archivedOnly) {
        if (!archived) return false;
      } else {
        if (archived) return false;
      }
      return true;
    })
    .sort((a, b) => {
      // Sort by selected field (descending)
      if (sortBy === 'progress') {
        // Sort by reading progress (descending: 100% -> 0%)
        const aProgress = getProgress(a.id);
        const bProgress = getProgress(b.id);
        const aPercent = (aProgress?.total_blocks > 0)
          ? (aProgress.read_indices?.length || 0) / aProgress.total_blocks
          : 0;
        const bPercent = (bProgress?.total_blocks > 0)
          ? (bProgress.read_indices?.length || 0) / bProgress.total_blocks
          : 0;
        if (aPercent !== bPercent) {
          return bPercent - aPercent; // Descending: 100% first
        }
        // Same progress: sort by created_at descending (newer first)
        return new Date(b.created_at) - new Date(a.created_at);
      }
      if (sortBy === 'last_viewed_at') {
        // For last_viewed_at, fallback to created_at if never viewed
        const aDate = new Date(a.last_viewed_at || a.created_at);
        const bDate = new Date(b.last_viewed_at || b.created_at);
        return bDate - aDate;
      }
      if (sortBy === 'rating') {
        // Sort by rating (5 to 0 descending)
        // If ratings are equal, fallback to created_at
        if (b.rating !== a.rating) {
          return (b.rating || 0) - (a.rating || 0);
        }
        return new Date(b.created_at) - new Date(a.created_at);
      }
      // Default: sort by created_at
      return new Date(b.created_at) - new Date(a.created_at);
    });
};

/**
 * Move article to a list (null = Inbox)
 */
export const moveArticleToList = (articleId, listId) => {
  articles$[articleId].assign({
    list_id: listId,
    updated_at: new Date().toISOString(),
  });
};

/**
 * Toggle article archive status (per-user, stored in readingProgress$)
 */
export const toggleArticleArchive = (articleId) => {
  toggleArchive(articleId);
};

/**
 * Update article title
 */
export const updateArticleTitle = (articleId, title) => {
  const trimmedTitle = title.trim();
  if (!trimmedTitle) return; // Don't allow empty titles
  articles$[articleId].assign({
    title: trimmedTitle,
    updated_at: new Date().toISOString(),
  });
};

/**
 * Update article content (for block deletion)
 * Writes directly to Supabase + contentCache, bypassing observable for content.
 * This fixes a pre-existing issue where excludeFieldsOnUpdate blocked content sync.
 * Note: Direct Supabase call — requires network. Shows warning if offline.
 */
export const updateArticleContent = async (articleId, content) => {
  const now = new Date().toISOString();

  // Update timestamp in observable (metadata only, triggers sync for updated_at)
  articles$[articleId].assign({
    updated_at: now,
  });

  // Write content directly to Supabase (bypasses excludeFieldsOnUpdate)
  try {
    const { error } = await supabase
      .from('reedle_articles')
      .update({ content, updated_at: now })
      .eq('id', articleId);
    if (error) {
      console.error('updateArticleContent: Supabase update failed:', error);
      // Lazy import to avoid circular dependency
      const { toast } = await import('sonner');
      toast.warning('Content change saved locally but failed to sync. It will sync when you reload online.');
    }
  } catch (e) {
    console.error('updateArticleContent: Supabase update failed:', e);
    const { toast } = await import('sonner');
    toast.warning('Content change saved locally but failed to sync. It will sync when you reload online.');
  }

  // Update local content cache, preserving article_status and other metadata fields
  const existing = await contentCacheGet(articleId);
  const ok = await contentCacheSet(articleId, {
    content,
    extra_metadata: existing?.extra_metadata || null,
    hidden_block_indices: existing?.hidden_block_indices || null,
    article_status: existing?.article_status || null,
  });
  if (ok) markCachedSync(articleId);
};

/**
 * Update last viewed timestamp (called when opening article reader)
 */
export const updateLastViewed = (articleId) => {
  const now = new Date().toISOString();
  articles$[articleId].assign({
    last_viewed_at: now,
    // Don't update updated_at - this is just tracking view time
  });
};

/**
 * Set article rating (1-5 stars)
 * Click same rating to remove (set to 0)
 * @param {string} articleId - Article ID
 * @param {number} rating - Rating value (1-5), or 0 to clear
 */
export const setArticleRating = (articleId, rating) => {
  const article = articles$[articleId].get();
  if (!article) return;

  // Validate rating (0-5)
  const validRating = Math.max(0, Math.min(5, Math.floor(rating)));

  // If clicking same rating, remove it (set to 0)
  const newRating = article.rating === validRating ? 0 : validRating;

  articles$[articleId].assign({
    rating: newRating,
    updated_at: new Date().toISOString(),
  });
};

/**
 * Create article from uploaded PDF.
 * Uploads PDF to Supabase Storage and creates a pending article record.
 * If pageRanges is provided (e.g. "1-200"), Mathpix will only process those pages.
 *
 * @param {File} file - PDF file
 * @param {string|null} listId - Target list (null = Inbox)
 * @param {string|null} pageRanges - Mathpix page_ranges string e.g. "1-200", null = all pages
 * @returns {Promise<Object>} Created article
 */
export const createPdfArticle = async (file, listId = null, pageRanges = null) => {
  const userId = auth$.user.get()?.id;
  if (!userId) throw new Error('User not authenticated');

  const fileId = uuidv4();
  const storagePath = `${userId}/${fileId}.pdf`;

  // 1. Upload PDF to Supabase Storage
  const { error: uploadError } = await supabase.storage
    .from('reedle-pdfs')
    .upload(storagePath, file, { contentType: 'application/pdf' });

  if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`);

  const id = uuidv4();
  const now = new Date().toISOString();
  const fileName = file.name.replace(/\.pdf$/i, '');
  // When a specific page range is selected via the gate modal, append it to the title
  // so multiple segments of the same PDF are easily distinguishable in the library.
  // pageRanges format is always "start-end" (e.g. "1-500"); replace hyphen with en-dash.
  const title = pageRanges
    ? `${fileName} (pp. ${pageRanges.replace('-', '–')})`
    : fileName;

  const newArticle = {
    id,
    user_id: userId,
    url: `storage:reedle-pdfs/${storagePath}`,
    title,
    content: '',
    excerpt: '',
    site_name: 'PDF',
    status: 'pending',
    error_message: null,
    is_starred: false,
    is_archived: false,
    rating: 0,
    list_id: listId,
    extra_metadata: pageRanges ? { page_ranges: pageRanges } : null,
    created_at: now,
    updated_at: now,
  };

  // Direct DB insert (same pattern as EPUB). Going through Legend-State would
  // silently drop the INSERT if localStorage is full, and would race with the
  // server's pending→ready status update by trying to push back the empty-content
  // client row via excludeFieldsOnUpdate (which does not cover `status`).
  const { error: insertError } = await supabase
    .from('reedle_articles')
    .insert(newArticle);
  if (insertError) {
    throw new Error(`Failed to create article: ${insertError.message}`);
  }
  // Do NOT call articles$[id].set() — realtime subscription will fetch it, and
  // the server's pending→ready transition with real content must not be overwritten.
  articlesVersion$.set(v => v + 1);

  // Track article saved event (after insert success)
  trackArticleSaved(newArticle, 'pdf');

  return newArticle;
};

/**
 * Create article from a PDF already uploaded to Supabase Storage.
 * Used by Zotero import — the Edge Function downloads the PDF and uploads to storage,
 * so we only need to create the article record pointing to the storage path.
 * @param {string} storagePath - e.g. "storage:reedle-pdfs/userId/fileId.pdf"
 * @param {{ title?: string, creators?: string[], date?: string, abstractNote?: string }} metadata
 * @param {string|null} listId - Target list (null = Inbox)
 * @returns {Object} Created article
 */
export const createPdfArticleFromStorage = async (storagePath, metadata = {}, listId = null) => {
  const userId = auth$.user.get()?.id;
  if (!userId) throw new Error('User not authenticated');

  const id = uuidv4();
  const now = new Date().toISOString();
  const title = metadata.title || 'Zotero PDF';

  const newArticle = {
    id,
    user_id: userId,
    url: storagePath,
    title,
    content: '',
    excerpt: metadata.abstractNote || '',
    site_name: 'PDF',
    status: 'pending',
    error_message: null,
    is_starred: false,
    is_archived: false,
    rating: 0,
    list_id: listId,
    created_at: now,
    updated_at: now,
  };

  // Direct DB insert (same pattern as EPUB/PDF) — must not silently drop on
  // localStorage quota failure, and must not race the server's pending→ready update.
  const { error: insertError } = await supabase
    .from('reedle_articles')
    .insert(newArticle);
  if (insertError) {
    throw new Error(`Failed to create article: ${insertError.message}`);
  }
  articlesVersion$.set(v => v + 1);
  trackArticleSaved(newArticle, 'zotero_pdf');

  return newArticle;
};

// Reedle server URL (for EPUB processing)
const REEDLE_SERVER_URL = apiConfig.renderJsUrl;

/**
 * Create article from uploaded EPUB
 * Uploads EPUB to Supabase Storage, creates pending article for process-epub Edge Function
 * @param {File} file - EPUB file
 * @param {string|null} listId - Target list (null = Inbox)
 * @returns {Promise<Object>} Created article
 */
export const createEpubArticle = async (file, listId = null) => {
  const userId = auth$.user.get()?.id;
  if (!userId) throw new Error('User not authenticated');

  const fileId = uuidv4();
  const storagePath = `${userId}/${fileId}.epub`;

  // 1. Upload EPUB to Supabase Storage
  const { error: uploadError } = await supabase.storage
    .from('reedle-epubs')
    .upload(storagePath, file, { contentType: 'application/epub+zip' });

  if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`);

  // 2. Store permanent storage path (not a signed URL that expires)
  const storageUrl = `storage:reedle-epubs/${storagePath}`;

  // 3. Create pending article first
  const id = uuidv4();
  const now = new Date().toISOString();
  const fileName = file.name.replace(/\.epub$/i, '');

  const newArticle = {
    id,
    user_id: userId,
    url: storageUrl, // Permanent path, never expires
    title: fileName,
    content: '',
    excerpt: '',
    site_name: 'EPUB',
    status: 'pending',
    error_message: null,
    is_starred: false,
    is_archived: false,
    rating: 0,
    list_id: listId,
    created_at: now,
    updated_at: now,
  };

  // Insert directly to database - bypasses Legend-State sync
  const { error: insertError } = await supabase
    .from('reedle_articles')
    .insert(newArticle);

  if (insertError) {
    throw new Error(`Failed to create article: ${insertError.message}`);
  }

  // IMPORTANT: Do NOT call articles$[id].set() here!
  // Legend-State would mark this as a local change and try to sync it back to DB,
  // which would overwrite the server's update (with real content) with empty content.
  //
  // Instead, the UI will show the article once syncedSupabase fetches it from DB
  // (which happens quickly due to realtime: true in config).
  // The realtime subscription will also receive updates when server finishes processing.

  // 4. Generate fresh signed URL for Express server to download
  const signedUrl = await resolveArticleUrl(storageUrl);

  // 5. Call Express server to process EPUB (don't await - let it process in background)
  fetch(`${REEDLE_SERVER_URL}/process-epub`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(), // Pass JWT for authentication
    },
    body: JSON.stringify({
      id,
      url: signedUrl,
      user_id: userId,
    }),
  }).then(async (response) => {
    if (!response.ok) {
      const error = await response.json();
      console.error('EPUB processing failed:', error);
    } else {
      console.log('EPUB processing completed');
    }
  }).catch((error) => {
    console.error('EPUB processing error:', error);
  });

  // Track article saved event
  trackArticleSaved(newArticle, 'epub');
  articlesVersion$.set(v => v + 1);

  return newArticle;
};

/**
 * Create article from uploaded audio file (MP3, M4A, WAV, OGG)
 * Uploads to Supabase Storage 'reedle-audio' bucket, creates pending article.
 * @param {File} file - Audio file
 * @param {string|null} listId - Target list (null = Inbox)
 * @returns {Promise<Object>} Created article
 */
export const createAudioArticle = async (file, listId = null) => {
  const userId = auth$.user.get()?.id;
  if (!userId) throw new Error('User not authenticated');

  const fileId = uuidv4();
  const ext = file.name.split('.').pop()?.toLowerCase() || 'mp3';
  const storagePath = `${userId}/${fileId}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from('reedle-audio')
    .upload(storagePath, file, { contentType: file.type || 'audio/mpeg' });

  if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`);

  const id = uuidv4();
  const now = new Date().toISOString();
  const title = file.name.replace(/\.[^.]+$/, ''); // strip extension

  const newArticle = {
    id,
    user_id: userId,
    url: `storage:reedle-audio/${storagePath}`,
    title,
    content: '',
    excerpt: '',
    site_name: 'Audio',
    audio_type: 'uploaded',
    status: 'pending',
    error_message: null,
    is_starred: false,
    is_archived: false,
    rating: 0,
    list_id: listId,
    created_at: now,
    updated_at: now,
  };

  articles$[id].set(newArticle);
  articlesVersion$.set(v => v + 1);

  trackArticleSaved(newArticle, 'audio');

  return newArticle;
};

// ============================================================================
// Hidden Blocks (Soft Delete) - Now stored in articles table (shared)
// ============================================================================

/**
 * Get hidden block indices for an article
 * @param {string} articleId - Article ID
 * @returns {number[]} Array of hidden block indices
 */
export const getArticleHiddenBlocks = (articleId) => {
  const article = articles$[articleId]?.get();
  return article?.hidden_block_indices || [];
};

/**
 * Update hidden block indices for an article
 * For inbox articles: only owner can update
 * For shared list articles: any list member can update
 * @param {string} articleId - Article ID
 * @param {number[]} hiddenIndices - Array of block indices to hide
 */
export const updateArticleHiddenBlocks = (articleId, hiddenIndices) => {
  const article = articles$[articleId]?.get();
  if (!article) {
    console.warn('updateArticleHiddenBlocks: Article not found', articleId);
    return;
  }

  articles$[articleId].assign({
    hidden_block_indices: hiddenIndices,
    updated_at: new Date().toISOString(),
  });
};

// ============================================================================
// Tag Management
// ============================================================================


/**
 * Update article tags
 * @param {string} articleId - Article ID
 * @param {string[]} tags - Array of tag strings (will be normalized)
 */
export const updateArticleTags = (articleId, tags) => {
  const article = articles$[articleId]?.get();
  if (!article) {
    console.warn('updateArticleTags: Article not found', articleId);
    return;
  }

  // Normalize: lowercase, trim, dedupe, filter empty
  const normalizedTags = [...new Set(
    tags.map(normalizeTag).filter(Boolean)
  )];

  articles$[articleId].assign({
    tags: normalizedTags,
    updated_at: new Date().toISOString(),
  });
};

/**
 * Add a tag to an article
 * @param {string} articleId - Article ID
 * @param {string} tag - Tag to add
 */
export const addArticleTag = (articleId, tag) => {
  const article = articles$[articleId]?.get();
  if (!article) return;

  const currentTags = article.tags || [];
  const normalizedTag = normalizeTag(tag);

  if (!normalizedTag || currentTags.includes(normalizedTag)) return;

  updateArticleTags(articleId, [...currentTags, normalizedTag]);
};

/**
 * Remove a tag from an article
 * @param {string} articleId - Article ID
 * @param {string} tag - Tag to remove
 */
export const removeArticleTag = (articleId, tag) => {
  const article = articles$[articleId]?.get();
  if (!article) return;

  const currentTags = article.tags || [];
  const normalizedTag = normalizeTag(tag);

  updateArticleTags(articleId, currentTags.filter(t => t !== normalizedTag));
};

/**
 * Get all unique tags across all articles with counts
 * @returns {Map<string, number>} Map of tag -> count
 */
export const getAllTagsWithCounts = () => {
  const articlesObj = articles$.get() || {};
  const tagCounts = new Map();

  Object.values(articlesObj).forEach(article => {
    if (!article || article.deleted || !article.tags) return;
    article.tags.forEach(tag => {
      tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
    });
  });

  return tagCounts;
};

/**
 * Get recent/frequent tags for suggestions (sorted by frequency)
 * @param {number} limit - Max tags to return
 * @returns {string[]} Array of tag names sorted by frequency
 */
export const getRecentTags = (limit = 20) => {
  const tagCounts = getAllTagsWithCounts();
  return [...tagCounts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([tag]) => tag);
};

// ============================================================================
// Lazy Content Loading (for slim sync optimization)
// ============================================================================

/**
 * Check if article has full content cached (synchronous).
 * Uses in-memory Set populated by initCachedIds() on startup.
 * @param {string} articleId - Article ID
 * @returns {boolean} True if content is in contentCache
 */
export const hasArticleContent = (articleId) => {
  return hasCachedContentSync(articleId);
};

/**
 * Check if article has full content cached (async, for accurate check).
 * @param {string} articleId - Article ID
 * @returns {Promise<boolean>}
 */
export const hasArticleContentAsync = async (articleId) => {
  const data = await contentCacheGet(articleId);
  return data !== null && data.content !== undefined && data.content !== null;
};

/**
 * Load full article content from database into contentCache.
 * Called on-demand when user opens an article without cached content.
 * Content goes to contentCache (separate IDB), NOT to articles$ observable.
 * @param {string} articleId - Article ID
 * @returns {Promise<{content: string, extra_metadata: object, hidden_block_indices: number[]}|null>}
 */
export const loadArticleContent = async (articleId) => {
  const article = articles$[articleId]?.get();
  if (!article) {
    console.warn('loadArticleContent: Article not found', articleId);
    return null;
  }

  // Check contentCache first.
  // Guard: if the cache was written while the article was 'pending' but the article is now
  // 'ready' (e.g. YouTube raw transcript cached before Gemini processing completed), treat
  // as a cache miss so we fetch the processed content from the DB.
  // Backward-safe: existing entries without article_status have undefined, not 'pending',
  // so they are never incorrectly invalidated.
  const cached = await contentCacheGet(articleId);
  if (cached && cached.content !== undefined && cached.content !== null) {
    const stale = cached.article_status === 'pending' && article.status === 'ready';
    if (!stale) {
      return cached;
    }
    console.log(`[articles$] loadArticleContent: invalidating stale pending cache for ${articleId}`);
  }

  // Don't load content while server is processing
  if (article.status === 'fetching' || article.status === 'pending') {
    return null;
  }

  try {
    const { data, error } = await supabase
      .from('reedle_articles')
      .select('status, content, extra_metadata, hidden_block_indices')
      .eq('id', articleId)
      .single();

    if (error) {
      console.error('loadArticleContent: Failed to fetch', error);
      return null;
    }

    // Update status in observable (metadata only, no content)
    if (data.status !== article.status) {
      articles$[articleId].assign({ status: data.status });
    }

    // Store content in separate cache (NOT in observable).
    // article_status is stamped so subsequent loads can detect stale pending→ready transitions.
    const contentData = {
      content: data.content,
      extra_metadata: data.extra_metadata,
      hidden_block_indices: data.hidden_block_indices,
      article_status: data.status,
    };
    const ok = await contentCacheSet(articleId, contentData);
    if (ok) markCachedSync(articleId);

    console.log(`Loaded content for article ${articleId}`);
    return contentData;
  } catch (err) {
    console.error('loadArticleContent: Error', err);
    return null;
  }
};

/**
 * Get article IDs that don't have content cached yet.
 * Uses in-memory Set (hasCachedContentSync) for fast synchronous lookup.
 * Used by background content fetcher.
 * @param {Object} options - Filter options
 * @param {number} options.limit - Max number of IDs to return
 * @param {string} options.priority - 'starred' | 'recent' | 'created'
 * @returns {string[]} Array of article IDs without cached content
 */
export const getArticlesWithoutContent = ({ limit = 50, priority = 'created' } = {}) => {
  const articlesObj = articles$.get() || {};

  return Object.values(articlesObj)
    .filter(article => {
      if (!article || !article.id || article.deleted) return false;
      // Skip articles still being processed - content not ready yet
      if (article.status === 'pending' || article.status === 'fetching') return false;
      // Filter to articles without cached content
      return !hasCachedContentSync(article.id);
    })
    .sort((a, b) => {
      // Priority sorting
      if (priority === 'starred') {
        // Starred first, then by created_at
        if (a.is_starred !== b.is_starred) {
          return a.is_starred ? -1 : 1;
        }
      } else if (priority === 'recent') {
        // Recently viewed first
        const aDate = new Date(a.last_viewed_at || a.created_at);
        const bDate = new Date(b.last_viewed_at || b.created_at);
        return bDate - aDate;
      }
      // Default: by created_at (newest first)
      return new Date(b.created_at) - new Date(a.created_at);
    })
    .slice(0, limit)
    .map(article => article.id);
};

/**
 * Load content for multiple articles (batch) into contentCache.
 * Content goes to contentCache (separate IDB), NOT to articles$ observable.
 * @param {string[]} articleIds - Array of article IDs
 * @returns {Promise<number>} Number of articles successfully loaded
 */
export const loadArticleContentBatch = async (articleIds, { signal } = {}) => {
  if (!articleIds || articleIds.length === 0) return 0;

  try {
    let query = supabase
      .from('reedle_articles')
      .select('id, status, content, extra_metadata, hidden_block_indices')
      .in('id', articleIds);

    // Allow caller to cancel in-flight request (e.g. on logout/update)
    if (signal) {
      query = query.abortSignal(signal);
    }

    const { data, error } = await query;

    if (error) {
      console.error('loadArticleContentBatch: Failed to fetch', error);
      return 0;
    }

    // Prepare entries for bulk content cache write
    const cacheEntries = [];
    data?.forEach(row => {
      if (articles$[row.id]) {
        // Update status in observable (metadata only, no content)
        const currentStatus = articles$[row.id].status?.get?.();
        if (row.status !== currentStatus) {
          articles$[row.id].assign({ status: row.status });
        }

        // Prepare content for cache
        cacheEntries.push({
          id: row.id,
          data: {
            content: row.content,
            extra_metadata: row.extra_metadata,
            hidden_block_indices: row.hidden_block_indices,
          },
        });
      }
    });

    // Skip cache write if abort was requested during fetch
    if (signal?.aborted) return 0;

    // Bulk write to contentCache (single IDB transaction)
    if (cacheEntries.length > 0) {
      const ok = await contentCacheBulkSet(cacheEntries);
      // Only update in-memory sync cache if IDB write succeeded
      if (ok) {
        for (const entry of cacheEntries) {
          markCachedSync(entry.id);
        }
      }
    }

    console.log(`Batch loaded content for ${cacheEntries.length} articles`);
    return cacheEntries.length;
  } catch (err) {
    console.error('loadArticleContentBatch: Error', err);
    return 0;
  }
};

/**
 * Get article content from cache, loading from DB if needed.
 * Convenience wrapper for components that need content.
 * @param {string} articleId - Article ID
 * @returns {Promise<{content: string, extra_metadata: object, hidden_block_indices: number[]}|null>}
 */
export const getArticleContent = async (articleId) => {
  // Try cache first
  const cached = await contentCacheGet(articleId);
  if (cached && cached.content !== undefined && cached.content !== null) {
    return cached;
  }

  // Not cached — load from DB
  return loadArticleContent(articleId);
};

// Re-export for modules that need direct cache access
export { initCachedIds } from '../lib/contentCache';
